/*
		       FILENAME: notification.service.ts
		          ROUTE: client/src/app/services/notification
	             AUTHOR: ICI/AD
 	               WHAT: WRAPS TOASTRSERVICE SO A TOAST CAN NEVER BE RAISED WITH AN EMPTY MESSAGE, AND SO SERVER ERROR TEXT IS PREFERRED OVER ANGULAR'S INTERNAL HTTP MESSAGE
	                HOW: RESOLVES EACH MESSAGE THROUGH A DATABASE LOOKUP, A BUILT-IN DEFAULT AND A CALLER FALLBACK BEFORE HANDING IT TO NGX-TOASTR
	    IMPORTING FILES: ngx-toastr, generic.service, default-messages
	  SUBSCRIBING FILES: helper.service, createusers.component, updateusers.component, banker-name.component
	   LAST COMMIT DATE: August 7, 2026
*/

import {inject, Injectable} from '@angular/core';
import {IndividualConfig, ToastrService} from 'ngx-toastr';
import {GenericService} from '../generic.service';
import {DEFAULT_ERROR_MESSAGE, DEFAULT_MESSAGES, DEFAULT_OFFLINE_MESSAGE} from '../messages/default-messages';

// ngx-toastr renders its message element behind *ngIf="message", so a falsy message produces
// a 300px red bar containing nothing but the icon. Everything here routes through resolve()
// so that cannot happen.
@Injectable ({ providedIn: 'root' })
export class NotificationService
{
	private readonly toastr = inject (ToastrService);
	private readonly generic = inject (GenericService);

	/**
	 * SHOW AN ERROR TOAST. NEVER EMPTY.
	 *
	 * @param message THE MESSAGE, WHICH MAY BE undefined OR A NON-STRING
	 * @param fallback SHOWN WHEN message HAS NOTHING USABLE IN IT
	 * @param options PASSED THROUGH TO NGX-TOASTR
	 */
	public error (message: unknown, fallback = DEFAULT_ERROR_MESSAGE, options?: Partial <IndividualConfig>): void
	{
		this.toastr.error (this.resolve (message, fallback), undefined, options);
	}

	/**
	 * SHOW A SUCCESS TOAST. NEVER EMPTY.
	 */
	public success (message: unknown, fallback = 'Done.', options?: Partial <IndividualConfig>): void
	{
		this.toastr.success (this.resolve (message, fallback), undefined, options);
	}

	/**
	 * SHOW A WARNING TOAST. NEVER EMPTY.
	 */
	public warning (message: unknown, fallback = 'Please review your input.', options?: Partial <IndividualConfig>): void
	{
		this.toastr.warning (this.resolve (message, fallback), undefined, options);
	}

	/**
	 * SHOW AN ERROR TOAST FOR A MSG-* SITE MESSAGE KEY.
	 *
	 * THE KEY IS RESOLVED THROUGH THE DATABASE FIRST, THEN THE BUILT-IN DEFAULTS. A KEY WITH NO
	 * ROW USED TO PRODUCE AN EMPTY TOAST.
	 */
	public errorFromKey (key: string, fallback?: string, options?: Partial <IndividualConfig>): void
	{
		this.error (this.generic.msg (key, ''), fallback ?? DEFAULT_MESSAGES[key] ?? DEFAULT_ERROR_MESSAGE, options);
	}

	/**
	 * SHOW AN ERROR TOAST FOR A FAILED HTTP CALL.
	 *
	 * PREFERS THE SERVER'S OWN SENTENCE AT err.error.message. err.message ON AN
	 * HttpErrorResponse IS ANGULAR'S OWN TEXT — IT LEAKS THE API URL AND TELLS THE MEMBER
	 * NOTHING THEY CAN ACT ON.
	 */
	public fromHttp (error: any, fallback = DEFAULT_ERROR_MESSAGE): void
	{
		// status 0 means the request never reached the server at all.
		if (error?.status === 0)
		{
			this.error ('', DEFAULT_OFFLINE_MESSAGE);
			return;
		}

		this.error (error?.error?.message, fallback);
	}

	/** CLEAR EVERY VISIBLE TOAST. */
	public clear (): void
	{
		this.toastr.clear ();
	}

	private resolve (message: unknown, fallback: string): string
	{
		const text = typeof message === 'string' ? message.trim () : '';

		return text.length ? text : fallback;
	}
}
